import { Box, Checkbox, Loader, Modal, Text, TextInput } from "@mantine/core";
import { IconChevronUp, IconSearch } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ACTIVITY_LAYERS, FREE_TEXT_SECTIONS, PROMPTING_SECTIONS } from "../../constants/activityLibrary";
import { saveDefaultPrompting } from "../../services/defaultPrompting";
import { generateQuickNoteAi } from "../../services/quickNoteAi";
import FieldResponseModal from "./FieldResponseModal";
import SupportMethodWizard from "./SupportMethodWizard";
import { closeBtnSx, ghostBtnSx, modalFooterSx, modalHeaderSx, primaryBtnSx } from "./styles";

// Care Plan Activities starts collapsed, matching the mockup.
const DEFAULT_COLLAPSED_LAYERS = new Set(["care_plan"]);

// hideCarePlan: true when the note isn't using a Support Plan ("Use Support
// Plan" = No) — Care Plan Activities only makes sense tied to a support plan,
// so it's left out of the picker entirely in that case.
const QuickNotesPicker = ({ opened, close, onInsert, hideCarePlan }) => {
  const { activities, defaultSupports } = useSelector((state) => state.activityLibrary);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Map()); // id -> { activity, response, supports }
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  const [collapsedLayers, setCollapsedLayers] = useState(DEFAULT_COLLAPSED_LAYERS);
  const [fieldBox, setFieldBox] = useState(null); // { activity, thenWizard }
  const [wizard, setWizard] = useState(null); // { activity, response }
  const [generating, setGenerating] = useState(false);

  // Support Methods is surfaced through the wizard, never as a top-level pick.
  const pickerLayers = useMemo(
    () => ACTIVITY_LAYERS.filter((l) => l.key !== "support_method" && !(hideCarePlan && l.key === "care_plan")),
    [hideCarePlan]
  );

  const active = useMemo(() => activities?.filter((a) => a.active) || [], [activities]);
  const q = search.trim().toLowerCase();

  // If Support Plan gets turned off while Care Plan items are already picked,
  // drop them — they're no longer a valid selection for this note.
  useEffect(() => {
    if (!hideCarePlan) return;
    setSelected((prev) => {
      const filtered = [...prev].filter(([, v]) => v.activity.layer !== "care_plan");
      return filtered.length === prev.size ? prev : new Map(filtered);
    });
  }, [hideCarePlan]);

  const toggleSection = (key) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleLayer = (key) => {
    setCollapsedLayers((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const commitSelection = (activity, response, supports) => {
    setSelected((prev) => {
      const next = new Map(prev);
      next.set(activity.id, { activity, response, supports });
      return next;
    });
  };

  const handleItemClick = (item) => {
    if (selected.has(item.id)) {
      setSelected((prev) => {
        const next = new Map(prev);
        next.delete(item.id);
        return next;
      });
      return;
    }

    const needsResponse = FREE_TEXT_SECTIONS.includes(item.section);
    const needsPrompting = item.layer === "care_plan" && PROMPTING_SECTIONS.includes(item.section);

    if (needsResponse) {
      // Community Based Activities (and Health Monitoring) only ever get the
      // free-text field — never the prompting wizard, even under Care Plan.
      setFieldBox({ activity: item, thenWizard: false });
    } else if (needsPrompting) {
      setWizard({ activity: item, response: "" });
    } else {
      commitSelection(item, "", []);
    }
  };

  const handleFieldSubmit = (value) => {
    const { activity, thenWizard } = fieldBox;
    setFieldBox(null);
    if (thenWizard) setWizard({ activity, response: value });
    else commitSelection(activity, value, []);
  };

  const handleWizardFinish = (supports) => {
    commitSelection(wizard.activity, wizard.response, supports);
    setWizard(null);
  };

  const closeHandler = () => {
    setSearch("");
    setSelected(new Map());
    setFieldBox(null);
    setWizard(null);
    close();
  };

  // POST /note/ai-response/quick-note — a separate, standalone endpoint.
  // Only fields the picker actually has are sent (activities + supportMethods);
  // clientName/shift/carePlanOutcome/clientResponse/progress aren't captured
  // here and are intentionally omitted rather than guessed.
  const handleAdd = async () => {
    // Map preserves insertion order, so activities are sent in the order the
    // DSP actually picked them.
    const items = [...selected.values()];
    const activitiesPayload = items.map(({ activity }) => ({
      category: activity.layer === "care_plan" ? activity.section : (ACTIVITY_LAYERS.find((l) => l.key === activity.layer)?.label || activity.section),
      activityType: activity.type,
      activity: activity.name,
    }));
    // Support methods only ever come from Care Plan Activities picks (the
    // wizard that collects them only opens for that layer).
    const supportMethods = [...new Set(items.flatMap(({ supports }) => supports || []))];

    const payload = { activities: activitiesPayload };
    if (supportMethods.length) payload.supportMethods = supportMethods;

    setGenerating(true);
    const result = await generateQuickNoteAi(payload);
    setGenerating(false);
    if (!result) return;

    // Real response shape: { aiReply, status }.
    const text = result?.aiReply || result?.data?.aiReply || result?.data?.note || result?.note || (typeof result?.data === "string" ? result.data : "");
    if (!text) return;

    // Hidden Activity Type (+ response/support metadata) travels along with the
    // inserted text so callers (AI note generation, reporting, analytics) can
    // use it without showing it to the DSP.
    onInsert(text, items.map(({ activity, response, supports }) => ({ name: activity.name, type: activity.type, response, supports })));
    closeHandler();
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={closeHandler}
        size={700}
        radius={12}
        zIndex={1400}
        overlayProps={{ blur: 3 }}
        padding={0}
        withCloseButton={false}
        styles={{ content: { fontFamily: "Inter" }, body: { padding: 0 } }}
      >
        <Box sx={modalHeaderSx}>
          <Text fz={16} fw={600} ff="Inter">Quick Notes</Text>
          <Box component="button" onClick={closeHandler} sx={closeBtnSx}>✕</Box>
        </Box>

        <Box sx={{ padding: "18px 22px", maxHeight: "60vh", overflow: "auto" }}>
          <Text fz={13} c="var(--app-text-muted)" mb={14} ff="Inter">
            Click an activity to choose the support methods used, then add it to the note.
          </Text>

          <Box
            sx={{
              display: "flex", alignItems: "center", gap: 9,
              border: "1px solid var(--app-border)", borderRadius: 10, padding: "0 13px", marginBottom: 16,
            }}
          >
            <IconSearch size={16} color="var(--app-text-muted)" />
            <TextInput
              placeholder="Search activities"
              variant="unstyled"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              styles={{ input: { fontSize: 14, fontFamily: "Inter", padding: "11px 0", color: "var(--app-text)" } }}
              sx={{ flex: 1 }}
            />
          </Box>

          {pickerLayers.map((layer) => {
            const layerItems = active.filter((a) => a.layer === layer.key && (!q || a.name.toLowerCase().includes(q)));
            const sections = [...new Set(layerItems.map((a) => a.section))];
            if (!sections.length) return null;

            const layerCollapsed = collapsedLayers.has(layer.key);

            return (
              <Box key={layer.key} mb={18} sx={{ border: "1px solid var(--app-border)", borderRadius: 12, background: "var(--app-surface)", padding: 14 }}>
                <Box
                  onClick={() => toggleLayer(layer.key)}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", paddingBottom: layerCollapsed ? 0 : 14 }}
                >
                  <Text fz={15} fw={700} ff="Inter" c="var(--app-text)">{layer.label}</Text>
                  <IconChevronUp size={18} color="var(--app-text)" style={{ transform: layerCollapsed ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
                </Box>

                {!layerCollapsed && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {sections.map((sectionName) => {
                      const key = `${layer.key}__${sectionName}`;
                      const isCollapsed = collapsedSections.has(key);
                      const items = layerItems
                        .filter((a) => a.section === sectionName)
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                      const maxLen = Math.max(...items.map((i) => i.name.length));
                      const cols = maxLen <= 20 && items.length >= 6 ? 3 : 2;

                      return (
                        <Box key={sectionName} sx={{ border: "1px solid var(--app-border)", borderRadius: 10, overflow: "hidden", background: "var(--app-surface)" }}>
                          <Box
                            onClick={() => toggleSection(key)}
                            sx={{
                              display: "flex", alignItems: "center", justifyContent: "space-between",
                              padding: "11px 12px", background: "var(--app-surface-2)", cursor: "pointer",
                              "&:hover": { filter: "brightness(.97)" },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <Text fz={12} fw={600} c="var(--app-text-muted)" ff="Inter" sx={{ textTransform: "uppercase", letterSpacing: ".03em" }}>
                                {sectionName}
                              </Text>
                              <Text
                                fz={11} fw={600} c="var(--app-text-muted)" ff="Inter"
                                sx={{ background: "var(--app-surface)", border: "1px solid var(--app-border)", borderRadius: 20, padding: "1px 8px" }}
                              >
                                {items.length}
                              </Text>
                            </Box>
                            <IconChevronUp size={16} color="var(--app-text-muted)" style={{ transform: isCollapsed ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
                          </Box>
                          {!isCollapsed && (
                            <Box sx={{ padding: 6, display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                              {items.map((item) => {
                                const sel = selected.get(item.id);
                                const isOn = !!sel;
                                const subParts = isOn ? [sel.response, (sel.supports || []).join(", ")].filter(Boolean) : [];

                                return (
                                  <Box
                                    key={item.id}
                                    onClick={() => handleItemClick(item)}
                                    sx={{
                                      display: "flex", alignItems: "flex-start", gap: 9,
                                      padding: 8, borderRadius: 6, cursor: "pointer",
                                      background: isOn ? "var(--app-tint-blue)" : "transparent",
                                      "&:hover": { background: isOn ? "var(--app-tint-blue)" : "var(--app-surface-2)" },
                                    }}
                                  >
                                    <Checkbox checked={isOn} readOnly size="sm" mt={2} sx={{ pointerEvents: "none" }} />
                                    <Box>
                                      <Text fz={14} ff="Inter" c="var(--app-text)">{item.name}</Text>
                                      {!!subParts.length && (
                                        <Text fz={11} c="var(--app-text-muted)" ff="Inter" mt={2}>{subParts.join(" · ")}</Text>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={modalFooterSx}>
          <Box component="button" onClick={closeHandler} sx={ghostBtnSx}>
            Cancel
          </Box>
          <Box
            component="button"
            onClick={handleAdd}
            disabled={!selected.size || generating}
            sx={{ ...primaryBtnSx(!selected.size || generating), display: "inline-flex", alignItems: "center", gap: 7 }}
          >
            {generating && <Loader size={14} color="var(--app-on-primary)" />}
            {generating ? "Generating..." : `Add to note${selected.size ? ` (${selected.size})` : ""}`}
          </Box>
        </Box>
      </Modal>

      <FieldResponseModal
        opened={!!fieldBox}
        activityName={fieldBox?.activity?.name}
        onCancel={() => setFieldBox(null)}
        onSubmit={handleFieldSubmit}
      />

      <SupportMethodWizard
        opened={!!wizard}
        activityKey={wizard?.activity?.id}
        activityName={wizard?.activity?.name}
        defaultSupports={defaultSupports}
        onClose={() => setWizard(null)}
        onFinish={handleWizardFinish}
        onSaveAsDefault={(picks) => saveDefaultPrompting(picks)}
      />
    </>
  );
};

export default QuickNotesPicker;
